import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, FileText, Receipt, Calendar, AlertTriangle, Wrench, Package, TrendingUp } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";

const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WO_LABELS = {
  checked_in: "Checked In", in_progress: "In Progress", quality_control: "QC",
  ready_for_pickup: "Ready", completed: "Completed", cancelled: "Cancelled",
};

export default function Reports() {
  const [d, setD] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [fDays, setFDays] = useState(30);
  useEffect(() => {
    CRM_API.get("/reports/summary").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    setFunnel(null);
    CRM_API.get("/reports/funnel", { params: { days: fDays } })
      .then(({ data }) => setFunnel(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [fDays]);

  if (!d) return <div className="p-10 text-white/40">Loading…</div>;

  const kpis = [
    { l: "REVENUE · 30 DAYS", v: money(d.revenue_30d), icon: DollarSign, tid: "rep-revenue-30d" },
    { l: "REVENUE · ALL TIME", v: money(d.revenue_total), icon: DollarSign, tid: "rep-revenue-total" },
    { l: "OUTSTANDING", v: money(d.outstanding), icon: Receipt, tid: "rep-outstanding", warn: d.outstanding > 0 },
    { l: "ESTIMATE PIPELINE", v: money(d.pipeline), icon: FileText, tid: "rep-pipeline" },
    { l: "APPOINTMENTS · 7 DAYS", v: d.appts_week, icon: Calendar, tid: "rep-appts" },
    { l: "LOW / OUT STOCK", v: `${d.inv_low} / ${d.inv_out}`, icon: AlertTriangle, tid: "rep-stock", warn: d.inv_low + d.inv_out > 0 },
  ];

  return (
    <div data-testid="crm-reports-page">
      <PageHeader eyebrow="Studio" title="Reports" subtitle="Revenue, pipeline, workload and stock at a glance." />

      <div className="px-6 md:px-10 py-8 space-y-10">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {kpis.map((k) => (
            <div key={k.l} data-testid={k.tid}
              className={`p-4 rounded-xl border ${k.warn ? "bg-amber-500/5 border-amber-500/25" : "bg-white/5 border-white/10"}`}>
              <k.icon className={`w-4 h-4 mb-3 ${k.warn ? "text-amber-400" : "text-[#1F5EFF]"}`} />
              <div className="font-display text-xl md:text-2xl text-white tabular-nums">{k.v}</div>
              <div className="mt-1 font-mono-alt text-[9px] tracking-widest text-white/40 uppercase">{k.l}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Work orders by status */}
          <section>
            <Sec title="Work orders by status" />
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(WO_LABELS).map(([k, l]) => (
                <div key={k} data-testid={`rep-wo-${k}`} className="p-3 rounded-lg bg-white/5 border border-white/10 text-center">
                  <div className="font-display text-2xl text-white tabular-nums">{d.wo_by_status[k] || 0}</div>
                  <div className="text-[9px] font-mono-alt uppercase tracking-widest text-white/40 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Estimates funnel */}
          <section>
            <Sec title="Estimates funnel" />
            <div className="grid grid-cols-3 gap-2">
              {["draft", "sent", "viewed", "approved", "declined", "expired"].map((s) => (
                <div key={s} data-testid={`rep-est-${s}`} className="p-3 rounded-lg bg-white/5 border border-white/10 text-center">
                  <div className="font-display text-2xl text-white tabular-nums">{d.est_by_status[s] || 0}</div>
                  <div className="text-[9px] font-mono-alt uppercase tracking-widest text-white/40 mt-1">{s}</div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Outstanding invoices */}
          <section>
            <Sec title={`Outstanding invoices (${d.open_invoices})`} />
            {d.outstanding_list.length === 0 ? (
              <Empty text="Nothing outstanding. Nice." />
            ) : (
              <div className="space-y-2">
                {d.outstanding_list.map((i) => (
                  <Link key={i.id} to={`/crm/invoices/${i.id}`} data-testid={`rep-inv-${i.id}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#1F5EFF]/40">
                    <div>
                      <div className="text-white text-sm font-medium">{i.invoice_number}</div>
                      <div className="text-white/40 text-[11px]">{i.customer_name}</div>
                    </div>
                    <div className="text-amber-300 font-mono-alt text-sm tabular-nums">{money(i.balance_due)}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Recent payments */}
          <section>
            <Sec title={`Recent payments (${d.payments_count})`} />
            {d.recent_payments.length === 0 ? (
              <Empty text="No payments recorded yet." />
            ) : (
              <div className="space-y-2">
                {d.recent_payments.map((p) => (
                  <div key={p.id} data-testid={`rep-pay-${p.id}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <div>
                      <div className="text-white text-sm font-medium">{p.customer_name}</div>
                      <div className="text-white/40 text-[11px]">{p.invoice_number} · {p.method} · {new Date(p.received_at).toLocaleDateString("en-CA")}</div>
                    </div>
                    <div className="text-emerald-300 font-mono-alt text-sm tabular-nums">+{money(p.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Packages & upsells funnel — internal calculator analytics */}
        <section data-testid="rep-funnel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Packages & upsells</span>
            </div>
            <div className="flex gap-1.5" data-testid="rep-funnel-range">
              {[7, 30, 90].map((n) => (
                <button key={n} onClick={() => setFDays(n)} data-testid={`rep-funnel-days-${n}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    fDays === n ? "bg-[#1F5EFF] border-[#1F5EFF] text-white" : "border-white/15 text-white/50 hover:text-white"}`}>
                  {n}d
                </button>
              ))}
            </div>
          </div>
          {!funnel ? (
            <div className="text-white/30 text-sm">Loading…</div>
          ) : funnel.events === 0 && funnel.aov.orders_with_package + funnel.aov.orders_without_package === 0 ? (
            <Empty text={`No calculator activity in the last ${fDays} days yet — events collect automatically as visitors use the calculator.`} />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { l: "PACKAGES ADDED", v: funnel.packages.reduce((t, p) => t + p.added, 0), icon: Package, tid: "rep-funnel-pkg-added" },
                  { l: "UPSELL NET ADDS", v: funnel.upsells.reduce((t, u) => t + u.net, 0), icon: TrendingUp, tid: "rep-funnel-upsell-net" },
                  { l: `AOV WITH PACKAGE (${funnel.aov.orders_with_package})`, v: funnel.aov.with_package != null ? money(funnel.aov.with_package) : "—", icon: DollarSign, tid: "rep-funnel-aov-with" },
                  { l: `AOV WITHOUT (${funnel.aov.orders_without_package})`, v: funnel.aov.without_package != null ? money(funnel.aov.without_package) : "—", icon: DollarSign, tid: "rep-funnel-aov-without" },
                ].map((k) => (
                  <div key={k.l} data-testid={k.tid} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <k.icon className="w-4 h-4 mb-3 text-[#1F5EFF]" />
                    <div className="font-display text-xl md:text-2xl text-white tabular-nums">{k.v}</div>
                    <div className="mt-1 font-mono-alt text-[9px] tracking-widest text-white/40 uppercase">{k.l}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="font-mono-alt text-[9px] tracking-widest text-white/35 uppercase mb-2">Packages — viewed → added</div>
                  {funnel.packages.length === 0 ? (
                    <Empty text="No package events yet." />
                  ) : (
                    <div className="space-y-1.5">
                      {funnel.packages.map((p) => (
                        <div key={p.key} data-testid={`rep-pkg-${p.key}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm truncate">{p.name}</div>
                            <div className="text-white/35 text-[11px] font-mono-alt">{p.viewed} viewed · {p.added} added</div>
                          </div>
                          {p.rate != null && (
                            <span className={`font-mono-alt text-xs tabular-nums ${p.rate >= 20 ? "text-emerald-300" : "text-white/60"}`}>
                              {p.rate}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-mono-alt text-[9px] tracking-widest text-white/35 uppercase mb-2">Upsells — viewed → added (removed)</div>
                  {funnel.upsells.length === 0 ? (
                    <Empty text="No upsell events yet." />
                  ) : (
                    <div className="space-y-1.5">
                      {funnel.upsells.map((u) => (
                        <div key={u.key} data-testid={`rep-upsell-${u.key}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm truncate">{u.name}</div>
                            <div className="text-white/35 text-[11px] font-mono-alt">
                              {u.viewed} viewed · {u.added} added{u.removed ? ` · ${u.removed} removed` : ""}
                            </div>
                          </div>
                          <span className={`font-mono-alt text-xs tabular-nums ${u.net > 0 ? "text-emerald-300" : u.net < 0 ? "text-red-300" : "text-white/60"}`}>
                            {u.net > 0 ? "+" : ""}{u.net}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {funnel.top_primary_services.length > 0 && (
                    <div className="mt-4" data-testid="rep-funnel-primaries">
                      <div className="font-mono-alt text-[9px] tracking-widest text-white/35 uppercase mb-2">Bookings by primary service</div>
                      <div className="flex flex-wrap gap-1.5">
                        {funnel.top_primary_services.map((s) => (
                          <span key={s.key} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/60">
                            {s.name} · {s.count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 text-white/30 text-[11px]">
                AOV = average order value from real quotes in the period. Attach % = added ÷ viewed (calculator events, may include test traffic).
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const Sec = ({ title }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-6 h-px bg-[#1F5EFF]" />
    <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">{title}</span>
  </div>
);

const Empty = ({ text }) => (
  <div className="p-8 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">{text}</div>
);
